import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPasswordDaysRemaining,
  getPasswordExpiresAt,
  needsPasswordChange,
} from "../auth/authSession";
import { formatExpiryDate } from "../utils/passwordRules";

const DISMISS_KEY = "procurement.passwordExpiryDismissed";

const PasswordExpiryNotice = () => {
  const navigate = useNavigate();
  const daysRemaining = getPasswordDaysRemaining();
  const expiresAt = getPasswordExpiresAt();
  const [open, setOpen] = useState(false);

  const warning = useMemo(() => {
    if (needsPasswordChange()) return null;
    if (daysRemaining == null || daysRemaining > 14 || daysRemaining <= 0) {
      return null;
    }
    const when = formatExpiryDate(expiresAt);
    const dayLabel = `${daysRemaining} day${daysRemaining === 1 ? "" : "s"}`;
    return {
      daysRemaining,
      title:
        daysRemaining <= 3
          ? "Password expires very soon"
          : "Password expires soon",
      message: when
        ? `Your password expires in ${dayLabel} (${when}). Update it now to avoid being locked out.`
        : `Your password expires in ${dayLabel}. Update it now to avoid being locked out.`,
    };
  }, [daysRemaining, expiresAt]);

  useEffect(() => {
    if (!warning || warning.daysRemaining > 7) return undefined;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === "1") return undefined;
    } catch {
      return undefined;
    }
    setOpen(true);
    return undefined;
  }, [warning]);

  if (!warning) return null;

  const goChange = () => {
    setOpen(false);
    navigate("/change-password", { state: { reason: "expiring" } });
  };

  const dismiss = () => {
    setOpen(false);
    try {
      sessionStorage.setItem(DISMISS_KEY, "1");
    } catch {
      // ignore
    }
  };

  return (
    <>
      <Alert
        severity={warning.daysRemaining <= 3 ? "error" : "warning"}
        sx={{
          borderRadius: 0,
          "& .MuiAlert-message": { width: "100%" },
        }}
        action={
          <Button color="inherit" size="small" onClick={goChange}>
            Change password
          </Button>
        }
      >
        {warning.message}
      </Alert>
      <Dialog open={open} onClose={dismiss} maxWidth="xs" fullWidth>
        <DialogTitle>{warning.title}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{warning.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={dismiss} color="inherit">
            Later
          </Button>
          <Button onClick={goChange} variant="contained">
            Change now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PasswordExpiryNotice;
